import Flickity from "flickity";
import "flickity/css/flickity.css";

import "flickity-fade";
import "flickity-fade/flickity-fade.css";

function parseCarouselSettings(carouselEl) {
  try {
    return JSON.parse(
      carouselEl.getAttribute("data-carousel-settings") || "{}",
    );
  } catch (error) {
    console.warn("Invalid data-carousel-settings JSON.", error);
    return null;
  }
}

function normalizeItemHeights(carouselItemEls) {
  if (!carouselItemEls.length) return;

  let tallestHeight = 0;

  carouselItemEls.forEach((carouselItemEl) => {
    tallestHeight = Math.max(tallestHeight, carouselItemEl.offsetHeight);
  });

  carouselItemEls.forEach((carouselItemEl) => {
    carouselItemEl.style.height = `${tallestHeight}px`;
  });
}

function buildFlickityOptions(carouselSettings) {
  const isFade = !!carouselSettings.fade;
  const isFreeScroll = !!carouselSettings.freeScroll && !isFade;
  const isAutoScroll =
    !!carouselSettings.autoScroll &&
    !isFade &&
    !isFreeScroll &&
    !carouselSettings.autoPlay;
  const isAutoPlay = !!carouselSettings.autoPlay && !isAutoScroll;

  return {
    isFade,
    isFreeScroll,
    isAutoScroll,
    isAutoPlay,
    flickityOptions: {
      accessibility: true,
      resize: true,
      percentPosition: true,
      setGallerySize: true,
      selectedAttraction: 0.008,
      friction: 0.15,
      fade: isFade,
      cellAlign:
        !isFade && !isFreeScroll
          ? carouselSettings.cellAlign || "center"
          : "left",
      contain: !isFade && !isFreeScroll ? !!carouselSettings.contain : false,
      freeScroll: isFreeScroll,
      wrapAround:
        typeof carouselSettings.wrapAround === "boolean"
          ? carouselSettings.wrapAround
          : true,
      draggable:
        typeof carouselSettings.draggable === "boolean"
          ? carouselSettings.draggable
          : true,
      prevNextButtons: false,
      pageDots:
        typeof carouselSettings.pageDots === "boolean"
          ? carouselSettings.pageDots
          : false,
      autoPlay: isAutoPlay ? carouselSettings.autoPlay : false,
      pauseAutoPlayOnHover:
        typeof carouselSettings.pauseAutoPlayOnHover === "boolean"
          ? carouselSettings.pauseAutoPlayOnHover
          : true,
      imagesLoaded:
        typeof carouselSettings.imagesLoaded === "boolean"
          ? carouselSettings.imagesLoaded
          : true,
    },
  };
}

function setupOverflow(carouselEl, carouselListEl, carouselSettings, isFade) {
  if (!carouselSettings.showOverFlow || isFade) return;

  const flickityViewportEl = carouselListEl.querySelector(".flickity-viewport");

  carouselListEl.style.overflow = "visible";

  if (flickityViewportEl) {
    flickityViewportEl.style.overflow = "visible";
  }
}

function setupNavigation(
  carouselEl,
  carouselNavEl,
  carouselPrevBtnEl,
  carouselNextBtnEl,
  flickityInstance,
  carouselSettings,
) {
  if (carouselNavEl) {
    carouselNavEl.style.display = carouselSettings.showNavigation ? "" : "none";
  }

  if (carouselPrevBtnEl) {
    carouselPrevBtnEl.addEventListener("click", () => {
      flickityInstance.previous();
    });
  }

  if (carouselNextBtnEl) {
    carouselNextBtnEl.addEventListener("click", () => {
      flickityInstance.next();
    });
  }

  if (flickityInstance.slides.length <= 1 && carouselNavEl) {
    carouselNavEl.style.display = "none";
  }
}

function setupAutoScroll(carouselEl, flickityInstance, carouselSettings) {
  let requestId = null;
  let isPaused = false;

  const autoScrollSpeed =
    typeof carouselSettings.autoScrollSpeed === "number"
      ? carouselSettings.autoScrollSpeed
      : 0.7;

  flickityInstance.x = 0;
  flickityInstance.positionSlider();

  function play() {
    if (isPaused) return;

    flickityInstance.x -= autoScrollSpeed;
    flickityInstance.positionSlider();

    requestId = window.requestAnimationFrame(play);
  }

  function start() {
    if (requestId) return;
    isPaused = false;
    requestId = window.requestAnimationFrame(play);
  }

  function pause() {
    isPaused = true;

    if (requestId) {
      window.cancelAnimationFrame(requestId);
      requestId = null;
    }
  }

  start();

  if (carouselSettings.pauseAutoPlayOnHover !== false) {
    carouselEl.addEventListener("mouseenter", pause);
    carouselEl.addEventListener("mouseleave", start);
    carouselEl.addEventListener("focusin", pause);
    carouselEl.addEventListener("focusout", start);
    carouselEl.addEventListener("touchstart", pause, { passive: true });
    carouselEl.addEventListener("touchend", start);
  }

  window.addEventListener("blur", pause);
  window.addEventListener("focus", start);

  return {
    start,
    pause,
    destroy() {
      pause();
      window.removeEventListener("blur", pause);
      window.removeEventListener("focus", start);
    },
  };
}

function initCarousel(carouselEl) {
  const carouselListEl = carouselEl.querySelector("[data-carousel-list]");
  const carouselItemEls = [
    ...carouselEl.querySelectorAll("[data-carousel-item]"),
  ];
  const carouselNavEl = carouselEl.querySelector(".carousel__nav");
  const carouselPrevBtnEl = carouselEl.querySelector("[data-carousel-prev]");
  const carouselNextBtnEl = carouselEl.querySelector("[data-carousel-next]");

  if (!carouselListEl) return null;

  const carouselSettings = parseCarouselSettings(carouselEl);
  if (!carouselSettings) return null;

  if (carouselSettings.disableDesktop && window.innerWidth > 992) {
    carouselEl.classList.remove("carousel");
    carouselListEl.classList.remove("carousel__list");
    carouselItemEls.forEach(function (carouselItemEl) {
      carouselItemEl.classList.remove("carousel__item");
    });
    carouselNavEl.remove();
    return null;
  }

  if (
    carouselSettings.disableTablet &&
    window.innerWidth > 769 &&
    window.innerWidth < 991
  ) {
    carouselEl.classList.remove("carousel");
    carouselListEl.classList.remove("carousel__list");
    carouselItemEls.forEach(function (carouselItemEl) {
      carouselItemEl.classList.remove("carousel__item");
    });
    carouselNavEl.remove();
    return null;
  }

  if (carouselSettings.disableMobile && window.innerWidth < 768) {
    carouselEl.classList.remove("carousel");
    carouselListEl.classList.remove("carousel__list");
    carouselItemEls.forEach(function (carouselItemEl) {
      carouselItemEl.classList.remove("carousel__item");
    });
    carouselNavEl.remove();
    return null;
  }

  carouselListEl.style.display = "block";

  normalizeItemHeights(carouselItemEls);

  const { isFade, isAutoScroll, flickityOptions } =
    buildFlickityOptions(carouselSettings);

  const flickityInstance = new Flickity(carouselListEl, flickityOptions);
  setupRelativeClasses(flickityInstance);

  setupOverflow(carouselEl, carouselListEl, carouselSettings, isFade);

  setupNavigation(
    carouselEl,
    carouselNavEl,
    carouselPrevBtnEl,
    carouselNextBtnEl,
    flickityInstance,
    carouselSettings,
  );

  let autoScrollControls = null;

  if (flickityInstance.slides.length > 1 && isAutoScroll) {
    autoScrollControls = setupAutoScroll(
      carouselEl,
      flickityInstance,
      carouselSettings,
    );
  }

  return {
    element: carouselEl,
    list: carouselListEl,
    flickity: flickityInstance,
    autoScroll: autoScrollControls,
    destroy() {
      if (autoScrollControls) {
        autoScrollControls.destroy();
      }

      flickityInstance.destroy();
    },
  };
}

function setupRelativeClasses(flickityInstance) {
  const updateClasses = () => {
    const cells = flickityInstance.cells;
    const cellCount = cells.length;
    const selectedIndex = flickityInstance.selectedIndex;
    const isWrapAround = flickityInstance.options.wrapAround;

    // 1. Clear ALL relative classes from every cell element
    cells.forEach((cell) => {
      cell.element.classList.remove(
        "is-prev",
        "is-prev-1",
        "is-prev-2",
        "is-next",
        "is-next-1",
        "is-next-2",
      );
    });

    // 2. Helper to find and tag cells
    const tagCell = (offset, classNames) => {
      let targetIndex;

      if (isWrapAround) {
        targetIndex = (selectedIndex + offset + cellCount) % cellCount;
      } else {
        targetIndex = selectedIndex + offset;
        if (targetIndex < 0 || targetIndex >= cellCount) return;
      }

      const targetCell = cells[targetIndex];
      if (targetCell && targetCell.element) {
        classNames.forEach((cls) => targetCell.element.classList.add(cls));
      }
    };

    // 3. Apply the classes
    tagCell(-2, ["is-prev", "is-prev-2"]);
    tagCell(-1, ["is-prev", "is-prev-1"]);
    tagCell(1, ["is-next", "is-next-1"]);
    tagCell(2, ["is-next", "is-next-2"]);
  };

  // Trigger on select (start of move) and settle (end of move)
  flickityInstance.on("select", updateClasses);
  flickityInstance.on("settle", updateClasses);

  // Initial run
  updateClasses();
}

function initCarousels() {
  const carouselEls = document.querySelectorAll("[data-carousel]");
  if (!carouselEls.length) return [];

  if (typeof Flickity === "undefined") {
    console.warn("Flickity is not loaded.");
    return [];
  }

  return [...carouselEls]
    .map((carouselEl) => initCarousel(carouselEl))
    .filter(Boolean);
}

document.addEventListener("DOMContentLoaded", () => {
  initCarousels();
});
